import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { GitHubIcon } from '@backstage/core-components';
import LogoFull from '../../assets/logo/LogoFull';
import LogoIcon from '../../assets/logo/LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import MemoryIcon from '@material-ui/icons/Memory';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
// Custom replacement for plugin-org's MyGroupsSidebarItem -- renders each
// team's GitHub avatar in the submenu (the upstream component applies one
// icon to every row).
import { MyTeamsSidebarItem } from './MyTeamsSidebarItem';
import { McpCatalogSidebarItem } from './McpCatalogSidebarItem';
import { TeamAdminSidebarItem } from './TeamAdminSidebarItem';
import { NetworkPolicySidebarItem } from './NetworkPolicySidebarItem';
import { KnowledgeGraphSidebarItem } from './KnowledgeGraphSidebarItem';
import { EnvDriftSidebarItem } from './EnvDriftSidebarItem';
import { ControlPanelSidebarItem } from './ControlPanelSidebarItem';
import { ViewAsSidebarItem } from './ViewAsSidebarItem';
import { ViewAsBanner } from '../viewAs/ViewAsBanner';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
// Permission imports
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  OnlineVisibilityPermission,
  ClaudeMetricsReadPermission,
} from '@internal/backstage-plugin-ocp-custom-permissions-backend';
import { BasicPermission } from '@backstage/plugin-permission-common';
const useSidebarLogoStyles = makeStyles(theme => ({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 18,
  },
  // Masthead lockup echoing the webapp-corp-handbook (LEVEL wordmark + a
  // small tracked product label to its right). The wordmark is sized down
  // from its standalone 80px so the label fits the open sidebar width
  // without widening the drawer.
  lockup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  productLabel: {
    // The sidebar is a dark surface in BOTH themes (navigation.background is
    // ink), so the label tracks the sidebar's own text color, not the page
    // text color (which is dark ink in light mode -> invisible here).
    color: (theme.palette as any).navigation?.color ?? 'rgba(241, 237, 227, 0.72)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    // Optically align the label's cap height with the wordmark.
    marginTop: 2,
  },
}));

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="LEVEL Backstage home">
        {isOpen ? (
          <span className={classes.lockup}>
            <LogoFull height={52} />
            <span className={classes.productLabel}>Backstage</span>
          </span>
        ) : (
          <LogoIcon />
        )}
      </Link>
    </div>
  );
};

const withPermission = (Component: React.ComponentType<any>, permissions: BasicPermission[]) => {
  return (props: any) => {
    return permissions.reduce((wrappedComponent, permission) => {
      return (
        <RequirePermission permission={permission} errorPage=' '>
          {wrappedComponent}
        </RequirePermission>
      );
    }, <Component {...props} />);
  };
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    {/* Submenu defaults are 202px wide with a 0ms close -- team names
        truncate and the flyout slams shut on diagonal pointer travel.
        320px fits the longest group name (claude-platform-maintainers +
        avatar); 250ms close grace keeps the panel up while the pointer
        crosses the gap. openDelay stays at the 100ms default. */}
    <Sidebar
      submenuOptions={{ drawerWidthOpen: 320 }}
      closeDelayMs={250}
    >
      <SidebarLogo />
      {/* Sole search entry point (the header search pill was removed by
          team decision -- it overlaid every page Header for one feature). */}
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        {withPermission(SidebarItem, [OnlineVisibilityPermission])({ icon: CategoryIcon, to: "/catalog", text: "Catalog" })}
        <McpCatalogSidebarItem />
        <SidebarItem icon={CreateComponentIcon} to="/create" text="Templates" end />
        <SidebarItem icon={AssignmentIcon} to="/create/tasks" text="My Tasks" />
        {/* The PR explore page (five modes, lens-aware) earned a nav slot:
            the team kept deep-linking it by hand. */}
        <SidebarItem icon={GitHubIcon} to="/pull-requests" text="Pull Requests" />
        <SidebarDivider />
        <SidebarScrollWrapper>
          {withPermission(SidebarItem, [OnlineVisibilityPermission])({ icon: ExtensionIcon, to: "/api-docs", text: "APIs" })}
          <SidebarItem icon={LibraryBooks} to="/docs" text="Docs" />
          <MyTeamsSidebarItem singularTitle="Team" pluralTitle="Teams" />
          <TeamAdminSidebarItem />
          <NetworkPolicySidebarItem />
          <EnvDriftSidebarItem />
          <ControlPanelSidebarItem />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      {/* AI surfaces, pinned as their own section above the account cluster. */}
      <SidebarGroup label="AI" icon={<MemoryIcon />}>
        <KnowledgeGraphSidebarItem />
        {withPermission(SidebarItem, [ClaudeMetricsReadPermission])({ icon: AssessmentIcon, to: "/claude-usage", text: "Claude Usage" })}
      </SidebarGroup>
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      {/* "View as" is an identity switch, so it sits with the account controls
          (gated; renders null when the user can't use the lens). */}
      <ViewAsSidebarItem />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings">
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    <ViewAsBanner />
    {children}
  </SidebarPage>
);
